import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useSessionGuard } from "@/hooks/useSessionGuard";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  sessionBlocked: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  sessionBlocked: false,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { blocked } = useSessionGuard(user);

  useEffect(() => {
    let mounted = true;
    let subscription: { unsubscribe: () => void } | null = null;

    const applySession = (session: Session | null) => {
      if (!mounted) return;
      setSession(session);
      // Only update user when identity actually changes, so token refreshes
      // (e.g. when returning to the tab) don't trigger downstream effects that
      // reset in-progress page state.
      setUser((prev) => {
        const next = session?.user ?? null;
        if (prev?.id === next?.id) return prev;
        return next;
      });
      setLoading(false);
    };

    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        applySession(session);
      } catch (error) {
        console.error("Auth session restore failed:", error);
        applySession(null);
      }

      if (!mounted) return;

      const { data } = supabase.auth.onAuthStateChange((_event, session) => {
        applySession(session);
      });
      subscription = data.subscription;
    };

    initAuth();

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, loading, sessionBlocked: blocked }}>
      {children}
    </AuthContext.Provider>
  );
};
